<script setup>
import dayjs from 'dayjs'
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { ABSENCE_ICON, useScheduleStore } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useNotify } from '@/composables/notify'
import { useStepper } from '@/composables/stepper'
import { useIsUpdated } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useEscapeClose } from '@/composables/keyboard'
import { useDetectDevice } from '@/composables/device'
import { useFilteredOptions } from '@/composables/select-filter'
import { useAbsenceCreate } from '@/composables/absence-create'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'
import SegmentedToggle from '@/components/shared/SegmentedToggle.vue'
import ServiceSelector from '@/components/shared/ServiceSelector.vue'
import AbsenceConflictDialog from '@/components/shared/AbsenceConflictDialog.vue'

const props = defineProps({
  service: {
    type: [Number, String],
    default: null,
  },
  vacation: {
    type: Boolean,
    default: false,
  },
  startDate: {
    type: String,
    default: null,
  },
  start: {
    type: String,
    default: null,
  },
  end: {
    type: String,
    default: null,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { step, markAttempted, wasAttempted } = useStepper()
const { notifySuccess } = useNotify()

const planningStore = usePlanningStore()
const scheduleStore = useScheduleStore()
const webSiteStore = useWebSiteStore()

const form = ref(null)
const isVacation = ref(props.vacation)
const startDay = props.startDate ?? dayjs().format('YYYY-MM-DD')
const toInputValue = (date) => dayjs(date).format('YYYY-MM-DDTHH:mm')
const editItem = ref({
  project: null,
  service: props.service ?? null,
  start: toInputValue(props.start ?? `${startDay}T${webSiteStore.getDefaultStartTime()}`),
  end: toInputValue(props.end ?? `${startDay}T${webSiteStore.getDefaultEndTime()}`),
})

const movementTypeOptions = computed(() => [
  { value: false, label: i18n.t('common.event', 1), icon: 'mdi-briefcase-outline' },
  { value: true, label: i18n.t('common.staff.absence'), icon: ABSENCE_ICON },
])

const onIsVacationChange = (value) => {
  isVacation.value = value
  if (value) editItem.value.project = null
}

const { isUpdated, isUpdating } = useIsUpdated(editItem)

onMounted(async () => {
  await Promise.all([
    planningStore.fetchProjectsAsync({ all: true }, true),
    planningStore.fetchStaffsAsync({ all: true }, true),
  ])
})

const { options: staffServices, onFilter: onServiceFilter } = useFilteredOptions(() => planningStore.staff.filter(s => !s.archived))

const availableProjects = computed(() => planningStore.projects
  .filter(p => !p.archived && !planningStore.isVacationProject(p.id)))

const selectedProject = computed(() => planningStore.getProjectById(editItem.value.project))

const onProjectChange = (projectId) => {
  editItem.value.project = projectId
  const project = planningStore.getProjectById(projectId)
  if (!props.start && project?.start && project?.end) {
    editItem.value.start = dayjs(project.start).format('YYYY-MM-DDTHH:mm')
    editItem.value.end = dayjs(project.end).format('YYYY-MM-DDTHH:mm')
  }
}

const step1HasError = computed(() => !editItem.value.service
  || !editItem.value.start
  || !editItem.value.end
  || (!isVacation.value && !editItem.value.project))

const { conflictDialog, saveAbsenceAsync } = useAbsenceCreate()

const onSaved = (createdEvent = null) => {
  notifySuccess(i18n.t('info.createdSuccessfully'))
  emit('update', { ...editItem.value, id: createdEvent?.id, isVacation: isVacation.value })
}

const onSave = async () => {
  isUpdating.value = true
  try {
    const start = dayjs(editItem.value.start).toISOString()
    const end = dayjs(editItem.value.end).toISOString()
    if (!isVacation.value) {
      await scheduleStore.syncRangeAsync(editItem.value.service, editItem.value.project, start, end,
        { quantity: 1, listingId: selectedProject.value?.listing ?? null })
      onSaved()
      return
    }
    await saveAbsenceAsync(editItem.value.service, start, end, onSaved)
  } finally {
    isUpdating.value = false
  }
}

const emit = defineEmits(['update', 'cancel'])
useNavigator(() => emit('cancel'))
useEscapeClose(isUpdated, () => emit('cancel'))
const onCancel = () => emit('cancel')
</script>
<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <navigate-back
      :text="isVacation ? i18n.t('common.createVacation') : i18n.t('common.newStaffAllocation')"
      @cancel="onCancel"
    />
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll">
      <q-card-section class="q-px-sm">
        <q-form ref="form">
          <q-stepper v-model="step" class="q-py-none">
            <q-step
              :name="1"
              :title="i18n.t('common.basicInfo')"
              :icon="wasAttempted(1) && step1HasError ? 'mdi-alert' : 'mdi-calendar-account'"
              :error="wasAttempted(1) && step1HasError"
              error-color="negative"
            >
              <div class="row q-col-gutter-sm">
                <div class="col-12">
                  <segmented-toggle
                    :model-value="isVacation"
                    :options="movementTypeOptions"
                    @update:model-value="onIsVacationChange"
                  />
                </div>
                <service-selector
                  v-model="editItem.service"
                  :class="isMobile || isVacation ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.staff')"
                  :options="staffServices"
                  use-input
                  :input-debounce="webSiteStore.getInputDebounce()"
                  :rules="[val => val != null || i18n.t('common.required')]"
                  @filter="onServiceFilter"
                />
                <q-select
                  v-if="!isVacation"
                  :model-value="editItem.project"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.project')"
                  option-value="id"
                  option-label="name"
                  :options="availableProjects"
                  emit-value
                  map-options
                  clearable
                  :rules="[val => val != null || i18n.t('common.required')]"
                  @update:model-value="onProjectChange"
                />
                <q-input
                  v-model="editItem.start"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.startDate')"
                  type="datetime-local"
                  required
                  :rules="[val => !!val || i18n.t('common.required')]"
                />
                <q-input
                  v-model="editItem.end"
                  :class="isMobile ? 'col-12' : 'col-6'"
                  :label="i18n.t('common.endDate')"
                  type="datetime-local"
                  :min="editItem.start"
                  required
                  :rules="[val => !!val || i18n.t('common.required')]"
                />
              </div>
              <stepper-navigation
                :finish="() => { markAttempted(1); form.validate().then(success => !step1HasError && success && onSave()) }"
                :finish-label="isVacation ? i18n.t('common.create') : i18n.t('common.assign')"
                :loading="isUpdating"
              />
            </q-step>
          </q-stepper>
        </q-form>
      </q-card-section>
    </q-card-section>
  </q-card>
  <absence-conflict-dialog
    v-model="conflictDialog.show"
    :conflicts="conflictDialog.conflicts"
    @remove="conflictDialog.onRemove()"
  />
</template>